import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {map, switchMap, withLatestFrom} from 'rxjs/operators';
import {
    PricingItemDeleteAction,
    PricingItemUpdateAction,
    QuoteActionTypes,
    QuoteServiceAppliedPriceUpdateAction,
    QuoteServiceUpdateAction,
    QuoteServiceUpdateSuccess,
    QuoteUpdateAction,
    QuoteUpdateSuccess,
    ServiceLineUpdateAction,
    ServiceLineUpdateSuccessAction
} from './quote.actions';
import {Store} from "@ngrx/store";
import {QuoteState} from "./quote.reducers";
import {getCurrentQuote, getReferential, getReferentialServices} from "../selectors";
import {QuoteServiceService} from "../../quote-service/quote-service.service";
import {PriceEngineService} from '../../price-engine/price-engine.service';
import {QuoteWrapper} from '../../models/quote/quote-wrapper';
import {EnumQuoteServiceSalesType} from '../../models/enum/enum-quote-service-sales-type';
import {ServiceLine} from '../../models/quote/service-line';
import {CustomGroup} from '../../models/quote/group/custom-group';
import {QuoteService} from '../../models/quote/quote-service';
import {DevicePricingItem} from '../../models/quote/pricing-item/device-pricing-item';

@Injectable()
export class QuoteEffects {

    public quoteServiceUpdateEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.QUOTE_SERVICE_UPDATE_ACTION),
                withLatestFrom(this.store.select(getReferential), this.store.select(getReferentialServices), this.store.select(getCurrentQuote)),
                map(([actionType, referential, referentialServices, wrapper]: [QuoteServiceUpdateAction, any, any, QuoteWrapper]) => {
                    console.time('quoteServiceUpdateEffect');


                    const quoteService = actionType.payload.quoteService;
                    let newQuoteService = quoteService;

                    if(quoteService.code === 'VT' && quoteService.salesType === EnumQuoteServiceSalesType.NEW) {

                        let quoteServiceUserInputs = {...quoteService.userInputs};
                        const totalNumberOfHolders = quoteServiceUserInputs['totalNumberOfHolders'] ?? 0;
                        const isManual = quoteServiceUserInputs['isManual'] ?? false;
                        quoteServiceUserInputs['totalNumberOfHolders'] = isManual ? totalNumberOfHolders : this.getTotalHoldersNumber(quoteService.customGroups);

                        newQuoteService = quoteService.copyWith({
                            userInputs: quoteServiceUserInputs
                        });

                        if (isManual) {
                            newQuoteService = this.quoteServiceService.calculateAdditionalDetails(newQuoteService);
                        }
                    }

                    if (actionType.payload.executeEffects) {
                        const code = newQuoteService.code;

                        let referentialService = referentialServices.find(referential => referential.serviceCode === code);
                        if (code === 'VT' && newQuoteService.salesType === EnumQuoteServiceSalesType.NEW) {
                            newQuoteService = this.quoteServiceService.synchronizeArticleOptions(newQuoteService, referentialService?.['options'] ?? []);
                            newQuoteService = this.quoteServiceService.synchronizeServiceOptions(newQuoteService, referentialService?.['options'] ?? [], false);
                        }
                        console.time('quoteServiceUpdateEffect executeEffects');
                        newQuoteService = this.priceEngine.getCorridors(newQuoteService, referential, referentialService, wrapper.countryIsoCode, wrapper.employeeRole?.pricingRole,  wrapper.regionLabel);
                        console.time('quoteServiceUpdateEffect executeEffects');
                        newQuoteService = this.quoteServiceService.calculateCorridors(newQuoteService, referential, referentialService, wrapper.countryIsoCode, wrapper.employeeRole);
                        console.timeEnd('quoteServiceUpdateEffect executeEffects');
                        newQuoteService = this.quoteServiceService.calculateAdditionalDetails(newQuoteService);
                    }
                    console.timeEnd('quoteServiceUpdateEffect');

                    return {
                        actionType: actionType,
                        quoteService: newQuoteService
                    };
                }),
                switchMap((object) => [new QuoteServiceUpdateSuccess({actionType: object.actionType, quoteService: object.quoteService})])
            );
    });


    private getTotalHoldersNumber(newCustomGroups: CustomGroup[]): number {
        return Math.max(
            0,
            ...newCustomGroups
                .flatMap(group => group.pricingItems)
                // OptionPricingItem n'étend pas DevicePricingItem : les options sont naturellement exclues
                .filter(item => item instanceof DevicePricingItem)
                .map(item => item.userInputs?.['quantity'] ?? 0)
        );
    }

    public quoteServiceUpdateSuccessEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.QUOTE_SERVICE_UPDATE_SUCCESS),
                withLatestFrom(this.store.select(getCurrentQuote)),
                map(([object, wrapper]: [QuoteServiceUpdateSuccess, QuoteWrapper]) => {

                    console.log(QuoteActionTypes.QUOTE_SERVICE_UPDATE_SUCCESS)
                    const quoteService = object.payload.quoteService;
                    let serviceLine = wrapper.serviceLines.find(serviceLine => serviceLine.code === quoteService.code);
                    if (quoteService.salesType === EnumQuoteServiceSalesType.NEW && serviceLine) {
                        serviceLine = serviceLine.copyWith({
                            newQuoteService: quoteService,
                        });
                    }
                    if (quoteService.salesType === EnumQuoteServiceSalesType.RENEWAL && serviceLine) {
                        serviceLine = serviceLine.copyWith({
                            renewalQuoteService: quoteService,
                        });
                    }
                    if (quoteService.salesType === EnumQuoteServiceSalesType.EXISTING && serviceLine) {
                        serviceLine = serviceLine.copyWith({
                            existingQuoteService: quoteService,
                        });
                    }

                    return {
                        serviceLine,
                        executeEffects: object.payload.actionType?.payload?.executeEffects,
                    };
                }),
                switchMap(({serviceLine, executeEffects}) => [new ServiceLineUpdateAction({serviceLine: serviceLine, executeEffects})])
            );
    });


    public quoteUpdateEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.QUOTE_UPDATE_ACTION),
                withLatestFrom(this.store.select(getReferentialServices)),
                map(([object, referentialServices]: [QuoteUpdateAction, any]) => {

                    console.log(QuoteActionTypes.QUOTE_UPDATE_ACTION)
                    let updatedWrapper = object.payload.wrapper;

                    if (object.payload.executeEffects) {
                        updatedWrapper = this.quoteServiceService.calculatePcts(updatedWrapper, referentialServices);
                        updatedWrapper = this.quoteServiceService.updateQuoteWrapperColors(updatedWrapper);
                    }

                    return updatedWrapper;
                }),
                switchMap((wrapper) => [new QuoteUpdateSuccess({wrapper})])
            )
    });

    public serviceLineUpdateEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.QUOTE_LINE_UPDATE_ACTION),
                map((object: ServiceLineUpdateAction) => {
                    console.time('serviceLineUpdateEffect');

                    console.log(QuoteActionTypes.QUOTE_LINE_UPDATE_ACTION)
                    let serviceLine = object.payload.serviceLine;
                    if(object.payload.executeEffects){
                        serviceLine = {...serviceLine} as ServiceLine;
                        serviceLine = this.quoteServiceService.updateServiceLine(serviceLine);
                    }
                    console.timeEnd('serviceLineUpdateEffect');

                    return {
                        serviceLine,
                        executeEffects: object.payload.executeEffects,
                    };
                }),
                switchMap(({serviceLine, executeEffects}) => [new ServiceLineUpdateSuccessAction({
                    serviceLine: serviceLine,
                    executeEffects
                })])
            )
    });

    public serviceLineUpdateSuccessEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.QUOTE_LINE_UPDATE_SUCCESS),
                withLatestFrom(this.store.select(getCurrentQuote)),
                map(([object, wrapper]: [ServiceLineUpdateSuccessAction, QuoteWrapper]) => {
                    console.log(QuoteActionTypes.QUOTE_LINE_UPDATE_SUCCESS)

                    const serviceLine = object.payload.serviceLine;
                    let number = wrapper.serviceLines.findIndex(serviceLine => serviceLine.code === object.payload.serviceLine.code);
                    let serviceLines = [...wrapper.serviceLines];

                    serviceLines[number] = serviceLine;
                    wrapper = wrapper.copyWith({
                        serviceLines: serviceLines
                    });
                    return {
                        wrapper,
                        executeEffects: object.payload.executeEffects,
                    };
                }),
                switchMap(({wrapper, executeEffects}) => [new QuoteUpdateAction({wrapper: wrapper, executeEffects})])
            );
    });

    public pricingItemUpdateEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.PRICING_ITEM_UPDATE_ACTION),
                withLatestFrom(this.store.select(getCurrentQuote)),
                map(([object, wrapper]: [PricingItemUpdateAction, QuoteWrapper]) => {

                    console.log(QuoteActionTypes.PRICING_ITEM_UPDATE_ACTION)

                    const newPricingItem = object.payload.pricingItem;
                    const codeService = object.payload.codeService;
                    const salesType = object.payload.salesType;

                    let newServiceLine = wrapper.getServiceLineByCode(codeService);
                    let newQuoteService = this.getQuoteServiceBySalesType(newServiceLine, salesType);
                    let customGroup = this.getCustomGroup(newQuoteService, newPricingItem.id);

                    if (customGroup !== undefined) {

                        const newPricingItems = customGroup.pricingItems.map(item =>
                            item.id === newPricingItem.id ? newPricingItem : item
                        )

                        const newCustomGroup = new CustomGroup().copyWith({
                            id: customGroup.id,
                            title: customGroup.title,
                            order: customGroup.order,
                            pricingItems: newPricingItems,
                            isDuplicated: customGroup.isDuplicated
                        });

                        const updatedCustomGroups = newQuoteService.customGroups.map(cg =>
                            cg.id === customGroup.id ? newCustomGroup : cg
                        );

                        newQuoteService = this.updateTotalNumberOfHolders(updatedCustomGroups, newQuoteService);

                        return {quoteService: newQuoteService.copyWith({customGroups: updatedCustomGroups}),  executeEffects: object.payload.executeEffects};
                    }

                    newQuoteService = this.updateTotalNumberOfHolders(newQuoteService.customGroups, newQuoteService);

                    return {quoteService: newQuoteService,  executeEffects: object.payload.executeEffects};
                }),
                switchMap(({quoteService, executeEffects}) => [new QuoteServiceUpdateAction({quoteService: quoteService, executeEffects: executeEffects})])
            );
    });


    private updateTotalNumberOfHolders(updatedCustomGroups: CustomGroup[], newQuoteService: QuoteService) {
        let currentMaxQuantity = this.getTotalHoldersNumber(updatedCustomGroups);
        let oldMaxQuantity = {...newQuoteService.userInputs}['maxQuantity'] ?? currentMaxQuantity;
        let isManual = currentMaxQuantity !== oldMaxQuantity ? false : {...newQuoteService.userInputs}['isManual'] ?? false;
        return newQuoteService.copyWith({
            userInputs: {
                totalNumberOfHolders: {...newQuoteService.userInputs}['totalNumberOfHolders'],
                isManual: isManual,
                maxQuantity: currentMaxQuantity
            }
        });
    }

    public pricingItemDeleteEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.PRICING_ITEM_DELETE_ACTION),
                withLatestFrom(this.store.select(getCurrentQuote)),
                map(([object, wrapper]: [PricingItemDeleteAction, QuoteWrapper]) => {
                    console.log(QuoteActionTypes.PRICING_ITEM_DELETE_ACTION)

                    const pricingItemToDelete = object.payload.pricingItem;
                    const codeService = object.payload.codeService;
                    const salesType = object.payload.salesType;

                    let newServiceLine = wrapper.getServiceLineByCode(codeService);
                    let newQuoteService = this.getQuoteServiceBySalesType(newServiceLine, salesType);
                    let customGroup = this.getCustomGroup(newQuoteService, pricingItemToDelete.id);

                    let newArticles = [...customGroup.pricingItems];
                    const articleIndex = customGroup.pricingItems.findIndex(element => element.id == pricingItemToDelete.id);
                    newArticles.splice(articleIndex, 1);
                    let newCustomGroups = newQuoteService.customGroups.map(_customGroup => {
                        if (_customGroup.id === customGroup.id) {
                            return new CustomGroup().copyWith({
                                id: customGroup.id,
                                title: customGroup.title,
                                order: customGroup.order,
                                pricingItems: newArticles,
                                isDuplicated: customGroup.isDuplicated
                            });
                        }
                        return _customGroup;
                    });
                    if (!newQuoteService.isCustomGroups && newCustomGroups.length > 0 && newCustomGroups[0].pricingItems.length == 0) {
                        newCustomGroups = [];
                    }

                    return newQuoteService.copyWith({customGroups: newCustomGroups});
                }),
                switchMap((quoteService) => [new QuoteServiceUpdateAction({quoteService: quoteService, executeEffects: true})])
            );
    });

    public quoteServiceAppliedPriceUpdateEffect = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(QuoteActionTypes.QUOTE_SERVICE_APPLIED_PRICE_UPDATE_ACTION),
                map((actionType: QuoteServiceAppliedPriceUpdateAction) => {
                    return this.quoteServiceService.updateServiceAppliedPrice(actionType.payload.quoteService, actionType.payload.appliedPrice);
                }),
                switchMap(currentQuoteService => [new QuoteServiceUpdateAction({quoteService: currentQuoteService, executeEffects: true})])
            );
    });

    private getCustomGroup(newServices: QuoteService, articleId: string) {
        return newServices.customGroups.find(customGroup =>
            customGroup.pricingItems.find(pricingItem => pricingItem.id === articleId));
    }
    private getQuoteServiceBySalesType(newServiceLine: ServiceLine, salesType: EnumQuoteServiceSalesType) {
        switch (salesType) {
            case EnumQuoteServiceSalesType.NEW:
                return newServiceLine.newQuoteService;
            case EnumQuoteServiceSalesType.EXISTING:
                return newServiceLine.existingQuoteService;
            case EnumQuoteServiceSalesType.RENEWAL:
                return newServiceLine.renewalQuoteService;
        }
    }

    constructor(
        private store: Store<QuoteState>,
        private actions$: Actions,
        private quoteServiceService: QuoteServiceService,
        private priceEngine: PriceEngineService,
    ) {
    }
}
